import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import {
  // Save,
  Check,
  Plane,
  FileText,
  Users,
  Settings,
  MapPin,
  ArrowRight,
  Trash2,
  MessageSquareText,
  Send,
  UserPlus,
  Building,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import {
  BufferConfig,
  getVenues,
  HotelPropertyApi,
  getHotelsByVenue,
} from "services/tourService";
import { CustomDayPicker } from "ui/CustomDayPicker";
import {
  calculateVenueSchedule,
  getIntercityCheckout,
  getTotalVenueBuffer,
  getTravelTimeMinutes,
  normalizeTime,
  parseTimeToMinutes,
} from "utils/venueScheduleCalculator";
import {
  getBufferConfigurations,
  VenueApi,
  PortApi,
  getPorts,
  getCutoffConfigurations,
  CutoffConfig,
  IntercityCheckoutApi,
  getIntercityCheckouts,
  getTourChats,
  tourChatsubmittion,
  TourChat,
  SendTourConversationPayload,
} from "services/tourService";
import { useAuth } from "contexts/AuthContext";
import Services from "./components/Services";
import Excursions from "./components/Excursions";
import Overview from "./components/Overview";
import { Switch } from "ui/switch";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  getRoomAllocations,
  getTourApproval,
  getVisaApplicants,
  shortlistApplicants,
  getShortlistedApplicants,
  getTourServices,
  saveTourServices,
  GetTourServicesResponse,
  TourApprovalResponse,
  VisaApplication,
  AssignedTourAtraaf,
  getAssignedTourAtraaf,
  TravelSegment,
  deleteTourService,
  RoomAllocationApplicant,
  getTourServiceMaster,
  TourServiceMasterItem,
  ShortlistedVisaApplication,
  deleteShortlistedApplicant,
  saveTourAtraaf,
} from "services/tourApprovalService";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "ui/accordion";
import VisaApplicantCard, { Applicant } from "./VisaApplicantCard";
import ApplicantTable from "./components/ApplicantTable";
import SelectApplicants from "./components/SelectApplicants";
import useDebounce from "hooks/useDebounce";
import { FaizAccommodation } from "./components/FaizAccommodation";
import RoomSummary from "./components/RoomSummary";
import {
  getAtraafFullDetails,
  TourAtraaf,
  getWalkInTourByPublicId,
  getWalkInRoomAllocations,
  WalkInTourDetailsResponse,
} from "services/tourWalkinService";

interface WalkInTourViewEditPageProps {
  onNavigate?: (page: string) => void;
  tourId?: string;
}

interface FormData {
  tourType: string;
  packageId?: string;
  packageName?: string;
  arrivalTransportMode: "flight" | "road" | "sea";
  arrivalAirport: number;
  arrivalDate: string;
  arrivalTime: string;
  arrivalFlightNumber: string;
  arrivalBookingRef: string;
  arrivalAirline: string;
  arrivalConfirmed: boolean;
  departureTransportMode: "flight" | "road" | "sea";
  departureAirport: number;
  departureDate: string;
  departureTime: string;
  departureFlightNumber: string;
  departureBookingRef: string;
  departureAirline: string;
  departureConfirmed: boolean;
  tourName: string;
  parentTourId: string;
  noOfApplicants: string;
  faizAccommodation: "yes" | "no";
  accommodationPreference: "minimum" | "maximum";
  specialAccommodationRequest: string;
  venues: Array<{
    id: string;
    venueId: number | null;
    venuePublicId?: string;
    venueName: string;
    hotelPropertyId: number | null;
    hotelName: string;
    fromDate: string;
    fromTime: string;
    toDate: string;
    toTime: string;
  }>;
}

interface ReviewMessage {
  id: string;
  sender: "backoffice" | "tour_operator";
  senderName: string;
  message: string;
  timestamp: string;
  dateTime: string;
}

interface Room {
  id: string;
  number: string;
  type: string;
  occupancy: number;
  maxOccupancy: number;
  requestedNumber: number;
  applicants: RoomAllocationApplicant[];
  venue: string;
  city: string;
  propertyPublicId: string;
  propertyRoomTypePublicId: string;
  roomSelectionPublicId: string;
  propertyRoomTypeId: number;
  propertyId: number;
  venueId: number;
  roomAllocationPublicId?: string;
  status: string;
}

enum TourType {
  GENERAL = 1,
  MIQAAT = 2,
}

type Excursion = {
  id: string;
  masterId?: string;
  name: string;
  time: string;
  type: "mandatory" | "included" | "optional" | "free";
  durationMinutes?: number;
};

export default function WalkInTourViewEditPage({
  onNavigate,
  tourId,
}: WalkInTourViewEditPageProps) {
  const [activeTab, setActiveTab] = useState<
    | "add-pax"
    | "arrival-departure"
    | "venue-summary"
    | "accommodation"
    | "room-allocation"
    | "services"
    | "itenary"
    | "overview"
  >("add-pax");
  const [approvalStatus] = useState<
    "saved" | "requested" | "approved" | "recommended" | "re-requested"
  >("recommended");

  // Arrival state
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const { setPageHeader } = usePageHeader();
  const [tourData, setTourData] = useState<WalkInTourDetailsResponse | null>(
    null,
  );
  const [tourApproval, setTourApproval] = useState<TourApprovalResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [roomSummaryLoading, setRoomSummaryLoading] = useState(false);
  const [, setVenueOptions] = useState<{ id: string; name: string }[]>([]);
  // Venue Summary state
  const [errors] = useState<Record<string, string>>({});
  const [venuesFromApi, setVenuesFromApi] = useState<VenueApi[]>([]);
  const [buffers, setBuffers] = useState<BufferConfig[]>([]);
  const [ports, setPorts] = useState<PortApi[]>([]);
  const [cutoffs, setCutoffs] = useState<CutoffConfig[]>([]);
  const [, setCutoffDates] = useState<string[]>([]);
  const [arrivalPortName, setArrivalPortName] = useState("");
  const [departurePortName, setDeparturePortName] = useState("");
  const [isVenueManuallyChanged, setIsVenueManuallyChanged] = useState(false);

  const [operatorExpiryDate] = useState<Date | null>(null);
  const [arrivalConfirmedFromApi, setArrivalConfirmedFromApi] = useState(false);
  const [departureConfirmedFromApi, setDepartureConfirmedFromApi] =
    useState(false);
  const [accommodateBy, setAccommodateBy] = useState<"minimum" | "maximum">(
    "maximum",
  );
  const [specialRoomRequestDetails, setSpecialRoomRequestDetails] =
    useState<string>("");

  const [visaApplications, setVisaApplications] = useState<VisaApplication[]>(
    [],
  );
  const [shortlistedApplicants, setShortlistedApplicants] = useState<
    ShortlistedVisaApplication[]
  >([]);
  const [tourServices, setTourServices] = useState<
    GetTourServicesResponse["data"] | null
  >(null);
  const [serviceMaster, setServiceMaster] = useState<TourServiceMasterItem[]>(
    [],
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [travelSegments, setTravelSegments] = useState<TravelSegment[]>([]);

  const [hotelsByVenue, setHotelsByVenue] = useState<
    Record<number, HotelPropertyApi[]>
  >({});

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState<Applicant | null>(
    null,
  );

  const [tourOperatorName, setTourOperatorName] = useState("");

  const [showApplicantDialog, setShowApplicantDialog] = useState(false);
  const [applicants, setApplicants] = useState<Applicant[]>([]);

  const [showSelectApplicantsDialog, setShowSelectApplicantsDialog] =
    useState(false);

  const [availableVisaApplicants, setAvailableVisaApplicants] = useState<
    Applicant[]
  >([]);

  const [loadingAvailableApplicants, setLoadingAvailableApplicants] =
    useState(false);
  const [applicantFilters, setApplicantFilters] = useState<{ searchText?: string }>({});
  const debouncedApplicantFilters = useDebounce(applicantFilters, 400);

  const getTourType = () => {
    return selectedMiqaat?.type === "package"
      ? TourType.MIQAAT
      : TourType.GENERAL;
  };

  const location = useLocation();
  const selectedMiqaat = location.state?.selectedMiqaat;
  const searchParams = new URLSearchParams(location.search);
  const previousBookingFilter = searchParams.get("bookingFilter") || "current";
  const [intercityCheckouts, setIntercityCheckouts] = useState<
    IntercityCheckoutApi[]
  >([]);
  const [tourAtraaf, setTourAtraaf] = useState<TourAtraaf[]>([]);
  const [assignedAtraaf, setAssignedAtraaf] = useState<AssignedTourAtraaf[]>(
    [],
  );
  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const mapTransportModeFromApi = (
    mode?: number,
  ): "flight" | "road" | "sea" => {
    switch (mode) {
      case 1:
        return "flight";
      case 2:
        return "road";
      case 3:
        return "sea";
      default:
        return "flight";
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMiqaat = getTourType() === TourType.MIQAAT;
  const [formData, setFormData] = useState<FormData>({
    tourType: "",
    arrivalTransportMode: "flight",
    arrivalAirport: 0,
    arrivalDate: "",
    arrivalTime: "",
    arrivalFlightNumber: "",
    arrivalBookingRef: "",
    arrivalAirline: "",
    arrivalConfirmed: false,
    departureTransportMode: "flight",
    departureAirport: 0,
    departureDate: "",
    departureTime: "",
    departureFlightNumber: "",
    departureBookingRef: "",
    departureAirline: "",
    departureConfirmed: false,
    tourName: "",
    parentTourId: "",
    noOfApplicants: "",
    faizAccommodation: "no",
    accommodationPreference: "maximum",
    specialAccommodationRequest: "",
    venues: [
      {
        id: "venue-1",
        venueId: null,
        venueName: "",
        hotelPropertyId: null,
        hotelName: "",
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
      {
        id: "venue-2",
        venueId: null,
        venueName: "",
        hotelPropertyId: null,
        hotelName: "",
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
    ],
  });

  const services = useMemo(() => {
    if (!tourServices) return [];
    const mandatory = tourServices.mandatoryServices.map((service) => ({
      id: service.serviceId.toString(),
      publicId: service.publicId,
      name: service.serviceName,
      type: "Included",
      price: 0,
      capacity: null,
    }));
    const optional = tourServices.optionalServices
      .flatMap((item) =>
        item.services.map((service) => ({
          id: service.serviceId.toString(),
          publicId: service.publicId,
          name: service.serviceName,
          type: "Optional",
          price: 0,
          capacity: null,
        })),
      )
      .filter(
        (service, index, self) =>
          self.findIndex((s) => s.id === service.id) === index,
      );
    return [...mandatory, ...optional];
  }, [tourServices]);

  const isInstantTour = tourData?.tourCategory === 1;
  const isExternalTour = formData.faizAccommodation === "no";

  // const isInstantTour = tourData?.tourType === "instant";
  const approvedApplicants = visaApplications.map((applicant) => ({
    id: applicant.visaApplicationId.toString(),
    name: `${applicant.firstName} ${applicant.lastName}`,
    itsNumber: applicant.itsNo,
    hofITS: applicant.hofITS,
    gender: applicant.gender,
    age: applicant.age,
    status: applicant.currentStatusCode,
    applicantType: applicant.applicantType,
    passportNo: applicant.passportNo,
    nationality: applicant.nationality,
    needsAccommodation: true,
    photo:
      "https://ui-avatars.com/api/?name=" +
      encodeURIComponent(`${applicant.firstName} ${applicant.lastName}`),
  }));

  const shortlistedApplicantList = shortlistedApplicants.map((applicant) => ({
    id: applicant.visaApplicationId.toString(),
    publicId: applicant.publicId,
    name: `${applicant.firstName} ${applicant.lastName}`,
    itsNumber: applicant.itsNo,
    hofITS: applicant.hofITS,
    gender: applicant.gender,
    age: applicant.age,
    status: applicant.currentStatusCode,
    applicantType: applicant.applicantType,
    passportNo: applicant.passportNo,
    nationality: applicant.nationality,
    needsAccommodation: true,
    photo:
      "https://ui-avatars.com/api/?name=" +
      encodeURIComponent(`${applicant.firstName} ${applicant.lastName}`),
  }));

  const formatDateSafe = (dateStr?: string) => {
    if (!dateStr) return "";

    const [datePart] = dateStr.split("T");
    return datePart;
  };

  const loadServiceMaster = async () => {
    try {
      const services = await getTourServiceMaster();
      setServiceMaster(services.filter((s) => s.isActive));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load service master.");
    }
  };

  const confirmDeleteApplicant = async () => {
    if (!applicantToDelete) return;

    if (!id) {
      toast.error("Tour ID is missing.");
      return;
    }

    const applicantId = String(applicantToDelete.id);

    // Find the shortlisted applicant using visaApplicationId
    const shortlistedApplicant = shortlistedApplicants.find(
      (applicant) => String(applicant.visaApplicationId) === applicantId,
    );

    if (!shortlistedApplicant?.publicId) {
      toast.error("Shortlisted applicant ID is missing.");
      return;
    }

    try {
      await deleteShortlistedApplicant(shortlistedApplicant.publicId);
      const response = await getShortlistedApplicants(id);
      setShortlistedApplicants(response.data);
      setApplicants(
        response.data.map(mapShortlistedVisaApplicationToApplicant),
      );
      setApplicantServices((prev) => {
        const next = { ...prev };
        delete next[applicantId];
        return next;
      });
      setCarUnits((prev) =>
        prev.map((car) => ({
          ...car,
          applicants: car.applicants.filter((id) => id !== applicantId),
        })),
      );
      toast.success("Applicant removed successfully.");
      setApplicantToDelete(null);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Failed to delete shortlisted applicant:", error);
      toast.error("Failed to remove applicant.");
    }
  };

  useEffect(() => {
    const loadHotels = async () => {
      const result: Record<number, HotelPropertyApi[]> = {};
      for (const venue of formData.venues) {
        if (!venue.venueId) continue;
        const selectedVenue = venuesFromApi.find(
          (v) => v.venueId === venue.venueId,
        );
        if (!selectedVenue) continue;
        result[venue.venueId] = await getHotelsByVenue(selectedVenue.publicId);
      }
      setHotelsByVenue(result);
    };

    loadHotels();
  }, [formData.venues, venuesFromApi]);

  const [tourStatus, setTourStatus] = useState<number | null>(null);
  const isCancelled = tourStatus === 6;

  const [faizAccommodation] = useState<"yes" | "no">("yes");
  // Applicants state
  const [, setSelectedApplicants] = useState<string[]>([]);
  const [groupLeader, setGroupLeader] = useState<string | null>("1");
  const loadTourChats = useCallback(
    async (showError = false) => {
      try {
        if (!id) return;
        const response = await getTourChats(id);
        const chats: ReviewMessage[] =
          response?.data?.map(
            (item: TourChat): ReviewMessage => ({
              id: item.publicId,
              sender: item.isAdmin ? "backoffice" : "tour_operator",
              senderName: item.senderName,
              message: item.message,
              timestamp: new Date(item.createdOn).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
              dateTime: new Date(item.createdOn).toLocaleDateString(),
            }),
          ) ?? [];
        setReviewConversation(chats);
      } catch (error) {
        console.error(error);
        if (showError) {
          toast.error("Failed to load chat.");
        }
      }
    },
    [id],
  );

  useEffect(() => {
    if (id) {
      loadTourChats(false);
    }
  }, [id, loadTourChats]);

  const handleSendReviewResponse = async () => {
    const message = responseToAdmin.trim();
    if (!message) {
      toast.error("Empty message is not allowed.");
      return;
    }
    if (!id) {
      toast.error("Tour ID is missing.");
      return;
    }
    try {
      setLoading(true);
      const payload: SendTourConversationPayload = {
        tourPublicId: id,
        message: message,
        isInternal: false,
      };
      const response = await tourChatsubmittion(payload);
      if (!response.success) {
        toast.error(response.message);
        return;
      }
      toast.success("Message sent successfully.");
      setResponseToAdmin("");
      await loadTourChats();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  const [reviewConversation, setReviewConversation] = useState<ReviewMessage[]>(
    [],
  );
  const [responseToAdmin, setResponseToAdmin] = useState("");
  useEffect(() => {
    setPageHeader({
      title: "Tour Details",
      subtitle: formData.tourName,
      onBack: async () => {
        try {
          setLoading(true);
          navigate(`/walkin-tour?bookingFilter=${previousBookingFilter}`);
        } finally {
          setLoading(false);
        }
      },
    });
    return () => setPageHeader(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.tourName,
    navigate,
    previousBookingFilter,
    setPageHeader,
    tourStatus,
  ]);

  const serviceApplicants = applicants.map((applicant) => ({
    id: String(applicant.id),
    name: [
      applicant.title,
      applicant.firstName,
      applicant.middleName,
      applicant.lastName,
    ]
      .filter(Boolean)
      .join(" "),
    itsNumber: applicant.itsNo || "-",
    age: Number(applicant.age),
    photo: "",
  }));

  const handleAddApplicants = async (
    applicantIds: string[],
  ): Promise<boolean> => {
    if (!id) {
      toast.error("Tour ID is missing.");
      return false;
    }

    if (applicantIds.length === 0) {
      return false;
    }

    try {
      await shortlistApplicants(id, {
        visaApplicantIds: applicantIds.map(Number),
      });
      const response = await getShortlistedApplicants(id);
      setShortlistedApplicants(response.data);
      setApplicants(
        response.data.map(mapShortlistedVisaApplicationToApplicant),
      );

      toast.success("Applicants shortlisted successfully.");

      return true;
    } catch (error) {
      console.error("Failed to shortlist applicants:", error);
      toast.error("Failed to shortlist applicants.");

      return false;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemoveApplicant = (applicantId: string) => {
    setSelectedApplicants((prev) => prev.filter((id) => id !== applicantId));
    if (groupLeader === applicantId) {
      setGroupLeader(null);
    }
    setApplicantServices((prev) => {
      const newServices = { ...prev };
      delete newServices[applicantId];
      return newServices;
    });
    toast.success("Applicant removed from tour");
  };

  // Services state — only optional/group services tracked per applicant (mandatory/included are global)
  const [applicantServices, setApplicantServices] = useState<
    Record<string, string[]>
  >({});
  //   const [draggedService, setDraggedService] = useState<string | null>(null);
  const [dragOverApplicant, setDragOverApplicant] = useState<string | null>(
    null,
  );

  // Group service (Private Car) units: each unit has a list of assigned applicant IDs
  const [carUnits, setCarUnits] = useState<
    { id: string; applicants: string[] }[]
  >([{ id: "car-1", applicants: [] }]);
  const [dragOverCar, setDragOverCar] = useState<string | null>(null);

  // Excursions state
  const [excursionsByDay, setExcursionsByDay] = useState<
    Record<number, Excursion[]>
  >({});

  const parseDurationMinutes = (value: string | number | undefined) => {
    if (value === undefined || value === null || value === "") return 0;
    if (typeof value === "number") {
      return Math.round(value * 60);
    }
    const normalized = String(value).trim();
    if (normalized.includes(":")) {
      const [hours = 0, minutes = 0] = normalized.split(":").map(Number);
      return hours * 60 + minutes;
    }
    const numeric = Number(normalized.replace(/[^\d.]/g, ""));
    return Number.isFinite(numeric) ? Math.round(numeric * 60) : 0;
  };

  useEffect(() => {
    if (!tourApproval) return;
    if (!assignedAtraaf.length) return;
    if (!tourAtraaf.length) return;
    const grouped: Record<number, Excursion[]> = {};
    assignedAtraaf.forEach((assigned) => {
      const assignedDate = assigned.venueDate.split("T")[0];
      const day =
        Math.floor(
          (new Date(assignedDate).getTime() -
            new Date(formData.arrivalDate).getTime()) /
            (1000 * 60 * 60 * 24),
        ) + 1;
      if (day <= 0) return;
      const master = tourAtraaf.find(
        (a) => a.publicId === assigned.atraafPublicId,
      );
      grouped[day] ??= [];
      grouped[day].push({
        id: assigned.publicId,
        masterId: assigned.atraafPublicId,
        name: assigned.atraafName,
        time: master
          ? `${master.venueName} · ${master.durationHours.substring(0, 5)}`
          : "",
        type: master?.optionalRoute ? "optional" : "included",
        durationMinutes: master
          ? parseDurationMinutes(master.durationHours)
          : 0,
      });
    });
    setExcursionsByDay(grouped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedAtraaf, tourAtraaf, tourApproval, formData.venues]);

  // Sidebar state for excursions
  const [sidebarCollapsed, setSidebarCollapsed] = useState({
    transport: false,
    mawaid: true,
    ziyarat: false,
    excursions: true,
  });

  const [draggedActivity, setDraggedActivity] = useState<{
    id: string;
    name: string;
    description: string;
    category: string;
  } | null>(null);

  const [dropTargetDay, setDropTargetDay] = useState<number | null>(null);

  // Room Allocation state
  const [rooms, setRooms] = useState<Room[]>([]);
  const handleFaizRoomsChange = useCallback(
    (
      faizRooms: {
        id: string;
        venueId: string;
        venueName: string;
        propertyId: string;
        propertyName: string;
        roomTypeId: string;
        roomType: string;
        roomNameId: string[];
        roomNames: string;
        capacity: number;
      }[],
    ) => {
      const mappedRooms: Room[] = faizRooms.map((room) => ({
        id: room.id,
        number: room.roomNames,
        type: room.roomType,
        occupancy: 0,
        maxOccupancy: room.capacity,
        requestedNumber: 1,
        applicants: [],
        venue: room.propertyName,
        city: room.venueName,
        propertyPublicId: room.propertyId,
        propertyRoomTypePublicId: room.roomTypeId,
        roomSelectionPublicId: "",
        propertyRoomTypeId: 0,
        propertyId: 0,
        venueId: 0,
        status: "Pending",
      }));

      setRooms(mappedRooms);
    },
    [],
  );
  const [cancelledRoomIds, setCancelledRoomIds] = useState<string[]>([]);
  const [isTourCancelled, setIsTourCancelled] = useState(false);

  //   const allocatedApplicantIds = useMemo(
  //     () => [
  //       ...new Set(
  //         rooms.flatMap((room) =>
  //           room.applicants.map((applicant) =>
  //             applicant.visaApplicationId.toString(),
  //           ),
  //         ),
  //       ),
  //     ],
  //     [rooms],
  //   );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const getStatusName = (status: number | null) => {
    switch (status) {
      case 1:
        return "Draft";

      case 2:
        return "Submitted";

      case 3:
        return "Alternative Dates Proposed";

      case 4:
        return "Resubmitted";

      case 5:
        return "Confirmed";

      case 6:
        return "Cancelled";

      case 7:
        return "Checked In";

      case 8:
        return "Checked Out";

      case 9:
        return "Completed";

      default:
        return "-";
    }
  };

  const getStatusMeta = (status: number | null) => {
    switch (status) {
      case 1:
        return {
          className: "bg-gray-100 text-gray-700 border-gray-300",
        };

      case 2:
        return {
          className: "bg-blue-100 text-blue-700 border-blue-300",
        };

      case 3:
        return {
          className: "bg-orange-100 text-orange-700 border-orange-300",
        };

      case 4:
        return {
          className: "bg-indigo-100 text-indigo-700 border-indigo-300",
        };

      case 5:
        return {
          className: "bg-green-100 text-green-700 border-green-300",
        };

      case 6:
        return {
          className: "bg-red-100 text-red-700 border-red-300",
        };

      case 7:
        return {
          className: "bg-cyan-100 text-cyan-700 border-cyan-300",
        };

      case 8:
        return {
          className: "bg-purple-100 text-purple-700 border-purple-300",
        };

      case 9:
        return {
          className: "bg-emerald-100 text-emerald-700 border-emerald-300",
        };

      default:
        return {
          className: "bg-gray-100 text-gray-700 border-gray-300",
        };
    }
  };

  const statusMeta = getStatusMeta(tourStatus);
  const statusName = getStatusName(tourStatus);

  const saveApplicantServices = async (
    updatedApplicantServices: Record<string, string[]>,
  ) => {
    if (!id) {
      toast.error("Tour ID is missing.");
      return false;
    }

    try {
      const payload = {
        mandatoryServiceIds:
          tourServices?.mandatoryServices.map((service) => service.serviceId) ??
          [],

        optionalServices: Object.entries(updatedApplicantServices)
          .filter(([, serviceIds]) => serviceIds.length > 0)
          .map(([applicantId, serviceIds]) => ({
            applicantId: Number(applicantId),
            servicesIds: serviceIds.map(Number),
          })),
      };

      await saveTourServices(id, Number(user?.id), payload);

      const servicesResponse = await getTourServices(id);

      setTourServices(servicesResponse.data);

      return true;
    } catch (error) {
      console.error("Failed to save services:", error);
      toast.error("Failed to save services.");
      return false;
    }
  };

  const handleRemoveService = async (
    applicantId: string,
    serviceId: string,
  ) => {
    const service = services.find((s) => s.id === serviceId);

    if (!service) {
      toast.error("Service not found.");
      return;
    }

    if (service.type === "Mandatory" || service.type === "Included") {
      toast.error("Cannot remove mandatory or included services");
      return;
    }

    if (!id) {
      toast.error("Tour ID is missing.");
      return;
    }

    if (!service.publicId) {
      toast.error("Service public ID is missing.");
      return;
    }

    try {
      const response = await deleteTourService(id, service.publicId);
      setTourServices(response.data);
      setApplicantServices((prev) => ({
        ...prev,
        [applicantId]:
          prev[applicantId]?.filter(
            (assignedServiceId) => assignedServiceId !== serviceId,
          ) || [],
      }));

      toast.success("Service removed successfully.");
    } catch (error) {
      console.error("Failed to delete service:", error);

      toast.error("Failed to remove service.");
    }
  };

  const handleServiceDragEnd = (event: DragEndEvent, applicantId: string) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setApplicantServices((prev) => {
      const currentServices = prev[applicantId] || [];

      const oldIndex = currentServices.findIndex(
        (serviceId: string) => serviceId === active.id,
      );

      const newIndex = currentServices.findIndex(
        (serviceId: string) => serviceId === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }

      return {
        ...prev,
        [applicantId]: arrayMove(currentServices, oldIndex, newIndex),
      };
    });

    toast.success("Service order updated");
  };

  const handleExcursionDragEnd = (event: DragEndEvent, day: number) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setExcursionsByDay((prev) => {
        const items = prev[day] || [];
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return {
          ...prev,
          [day]: arrayMove(items, oldIndex, newIndex),
        };
      });
      toast.success("Excursion order updated");
    }
  };

  // Activity drag handlers (from sidebar)
  const handleActivityDragStart = (
    activity: { id: string; name: string; description: string },
    category: string,
  ) => {
    setDraggedActivity({ ...activity, category });
  };

  const handleActivityDragEnd = () => {
    setDraggedActivity(null);
    setDropTargetDay(null);
  };

  const handleDayDragOver = (e: React.DragEvent, day: number) => {
    e.preventDefault();
    setDropTargetDay(day);
  };

  const handleDayDragLeave = () => {
    setDropTargetDay(null);
  };

  const handleDayDrop = (e: React.DragEvent, day: number) => {
    e.preventDefault();
    if (draggedActivity) {
      const newExcursion: Excursion = {
        id: `${draggedActivity.category}-${Date.now()}`,
        name: draggedActivity.name,
        time: draggedActivity.description,
        type: day === 1 ? "mandatory" : "included",
      };
      setExcursionsByDay((prev) => ({
        ...prev,
        [day]: [...(prev[day] || []), newExcursion],
      }));
      toast.success(`${draggedActivity.name} added to Day ${day}`);
    }
    setDraggedActivity(null);
    setDropTargetDay(null);
  };

  const loadRoomAllocations = useCallback(
    async (tourData: WalkInTourDetailsResponse) => {
      if (!id) return;

      const allocationRes = await getRoomAllocations(id);
      const allocations = allocationRes.data.data.roomAllocations;

      const mappedRooms: Room[] = tourData.roomAvailabilities.flatMap(
        (availability) =>
          availability.properties.flatMap((property) =>
            property.rooms.flatMap((room) => {
              const matchingAllocations = allocations.filter(
                (a) => a.roomSelectionPublicId === room.roomSelectionPublicId,
              );

              return Array.from(
                { length: Number(room.requestedNumber ?? 0) },
                (_, index) => {
                  const allocation = matchingAllocations[index];

                  return {
                    id: `${property.propertyId}-${room.propertyRoomTypeId}-${index + 1}`,
                    number: (index + 1).toString(),
                    type: room.roomTypeName ?? "",
                    occupancy: allocation?.applicants?.length ?? 0,
                    maxOccupancy: Number(room.maxCapacity ?? 0),
                    requestedNumber: Number(room.requestedNumber ?? 0),

                    applicants:
                      allocation?.applicants?.map((a) => ({
                        tourRoomAllocationApplicantId:
                          a.tourRoomAllocationApplicantId,
                        publicId: a.publicId,
                        tourRoomAllocationId: a.tourRoomAllocationId,
                        visaApplicationId: a.visaApplicationId,
                        itsNo: a.itsNo,
                        applicantName: a.applicantName,
                        isActive: a.isActive,
                      })) ?? [],

                    venue: property.propertyName ?? "",
                    city: availability.venueName ?? "",

                    roomSelectionPublicId: room.roomSelectionPublicId ?? "",

                    propertyId: Number(property.propertyId),
                    propertyPublicId: property.propertyPublicId ?? "",

                    propertyRoomTypeId: Number(room.propertyRoomTypeId),
                    propertyRoomTypePublicId:
                      room.propertyRoomTypePublicId ?? "",

                    venueId: Number(availability.venueId),

                    roomAllocationPublicId: allocation?.publicId,

                    status: allocation?.status ?? "Pending",
                  };
                },
              );
            }),
          ),
      );

      setRooms(mappedRooms);
    },
    [id],
  );

  const reloadRoomAllocations = useCallback(async () => {
    if (!id) return;

    try {
      await getWalkInRoomAllocations(id);
      const latestTour = await getWalkInTourByPublicId(id);
      setTourData(latestTour);
      await loadRoomAllocations(latestTour);
    } catch (error) {
      console.error("Failed to reload room allocations:", error);
      throw error;
    }
  }, [id, loadRoomAllocations]);

  const reloadShortlistedApplicants = useCallback(async () => {
    if (!id) return;

    const response = await getShortlistedApplicants(id);

    setShortlistedApplicants(response.data);

    setApplicants(
      response.data.map(mapShortlistedVisaApplicationToApplicant),
    );
  }, [id]);

  

  useEffect(() => {
    if (activeTab !== "room-allocation") return;

    if (!id) {
      setRoomSummaryLoading(false);
      return;
    }

    let cancelled = false;

    const reloadRoomSummary = async () => {
      try {
        setRoomSummaryLoading(true);

        await Promise.all([
          reloadRoomAllocations(),
          reloadShortlistedApplicants(),
        ]);
      } catch (error) {
        console.error("Failed to reload room summary:", error);
      } finally {
        if (!cancelled) {
          setRoomSummaryLoading(false);
        }
      }
    };

    reloadRoomSummary();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    id,
    reloadRoomAllocations,
    reloadShortlistedApplicants,
  ]);
  
  useEffect(() => {
    const fetchTour = async () => {
      if (!venuesFromApi.length) return;
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const storedTours = JSON.parse(
          localStorage.getItem("walkinTours") || "[]",
        );

        const localTour = Array.isArray(storedTours)
          ? storedTours.find(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (tour: any) => tour.id === id || tour.publicId === id,
            )
          : null;

        if (localTour) {
          console.log("Loading local walk-in tour:", localTour);

          setTourData(localTour as WalkInTourDetailsResponse);

          mapLocalTourToState(localTour);

          // Services
          await loadServiceMaster();

          setTourApproval(null);
          setTourServices(null);

          try {
            const atraafRes = await getAtraafFullDetails();
            setTourAtraaf(atraafRes.data);
          } catch (error) {
            console.error("Failed to load Atraaf Master:", error);
            setTourAtraaf([]);
          }
          setAssignedAtraaf([]);
          setTravelSegments([]);

          if (Array.isArray(localTour.applicants)) {
            setApplicants(localTour.applicants);
          } else {
            setApplicants([]);
          }

          setVisaApplications([]);
          setShortlistedApplicants([]);

          setLoading(false);
          return;
        }

        await loadServiceMaster();

        const data = await getWalkInTourByPublicId(id);

        setTourData(data);
        mapApiToState(data);

        if (data.tourCategory !== 4) {
          await loadRoomAllocations(data);
        }

        const approval = await getTourApproval(id);
        setTourApproval(approval);

        const servicesResponse = await getTourServices(id);
        setTourServices(servicesResponse.data);

        const atraafRes = await getAtraafFullDetails();
        setTourAtraaf(atraafRes.data);

        const res = await getAssignedTourAtraaf(id);

        setAssignedAtraaf(res.data.assignments);
        setTravelSegments(res.data.travelSegments);

        const visaRes = await getVisaApplicants(id);
        setVisaApplications(visaRes.data);

        const shortlistRes = await getShortlistedApplicants(id);

        setShortlistedApplicants(shortlistRes.data);

        setApplicants(
          shortlistRes.data.map(mapShortlistedVisaApplicationToApplicant),
        );

        const shortlistedApplicantIds = new Set(
          shortlistRes.data.map((applicant) =>
            String(applicant.visaApplicationId),
          ),
        );

        const applicantServicesMap: Record<string, string[]> = {};

        servicesResponse.data.optionalServices.forEach((applicant) => {
          const applicantId = String(applicant.applicantId);

          if (!shortlistedApplicantIds.has(applicantId)) {
            return;
          }

          applicantServicesMap[applicantId] = applicant.services.map(
            (service) => String(service.serviceId),
          );
        });

        setApplicantServices(applicantServicesMap);
      } catch (err) {
        console.error("fetchTour failed", err);
        toast.error("Failed to load tour");
      } finally {
        setLoading(false);
      }
    };

    fetchTour();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, venuesFromApi]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues();
        setVenuesFromApi(res);
        const mapped = res.map((v) => ({
          id: String(v.venueId),
          name: v.venueName,
        }));
        setVenueOptions(mapped);
      } catch {
        toast.error("Failed to load venues");
      }
    };
    fetchVenues();
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    if (id) return;
    if (isConfirmed && !isVenueManuallyChanged) return;
    if (getTourType() === TourType.MIQAAT) return;
    if (formData.venues.some((v) => !v.venueId)) return;
    if (isVenueManuallyChanged) return;
    const updatedVenues = calculateVenueSchedule({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });
    if (!updatedVenues) return;
    const isSame =
      JSON.stringify(formData.venues) === JSON.stringify(updatedVenues);
    if (isSame) return;
    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v,
        ...updatedVenues[i],
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    formData.arrivalDate,
    formData.arrivalTime,
    formData.departureDate,
    formData.departureTime,
    buffers,
    intercityCheckouts,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formData.venues),
  ]);

  useEffect(() => {
    const fetchBuffers = async () => {
      try {
        const data = await getBufferConfigurations();
        setBuffers(data.filter((b) => b.isActive));
      } catch (e) {
        console.error("Buffer fetch failed", e);
      }
    };
    fetchBuffers();
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    if (formData.arrivalConfirmed) return;
    setIsVenueManuallyChanged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.arrivalAirport]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!venuesFromApi.length || !formData.arrivalAirport) return;
    if (formData.arrivalConfirmed) return;
    if (isVenueManuallyChanged) return;
    const route = getBaseRoute(formData.arrivalAirport);
    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v,
        venueId: route[i] || v.venueId,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    formData.arrivalAirport,
    venuesFromApi,
    isVenueManuallyChanged,
  ]);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const data = await getPorts();
        setPorts(data.filter((p) => p.isActive));
      } catch (e) {
        console.error("Port fetch failed", e);
      }
    };
    fetchPorts();
  }, []);

  useEffect(() => {
    const fetchCutoffs = async () => {
      try {
        const data = await getCutoffConfigurations();
        setCutoffs(data);
      } catch (e) {
        console.error("Cutoff fetch failed", e);
      }
    };
    fetchCutoffs();
  }, []);

  useEffect(() => {
    if (!cutoffs.length) return;
    const dates: string[] = [];
    cutoffs.forEach((c) => {
      const [year, month, day] = c.effectiveFrom
        .split("T")[0]
        .split("-")
        .map(Number);
      const start = new Date(year, month - 1, day);
      for (let i = 0; i < c.daysBeforeCutoff; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        dates.push(`${yyyy}-${mm}-${dd}`);
      }
    });
    setCutoffDates(dates);
  }, [cutoffs]);

  useEffect(() => {
    const fetchIntercity = async () => {
      try {
        const data = await getIntercityCheckouts();
        setIntercityCheckouts(data.filter((i) => i.isActive));
      } catch (e) {
        console.error("Intercity fetch failed", e);
      }
    };
    fetchIntercity();
  }, []);

  useEffect(() => {
    if (!selectedMiqaat) return;
    setFormData((prev) => {
      if (prev.arrivalDate || prev.departureDate) {
        return prev;
      }
      return {
        ...prev,
        arrivalDate: selectedMiqaat.arrivalDate?.split("T")[0] || "",
        departureDate: selectedMiqaat.departureDate?.split("T")[0] || "",
      };
    });
  }, [selectedMiqaat]);

  useEffect(() => {
    setIsVenueManuallyChanged(false);
  }, [formData.arrivalDate, formData.departureDate]);
  const [, setRefreshKey] = useState(0);
  const isConfirmed = formData.arrivalConfirmed || formData.departureConfirmed;
  const getBaseRoute = (arrivalPortId: number) => {
    const najafVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("najaf"),
    )?.venueId;
    const karbalaVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("karbala"),
    )?.venueId;
    if (!najafVenue || !karbalaVenue) return [];
    const arrivalPort = ports.find((p) => p.portId === arrivalPortId);
    if (!arrivalPort) return [];
    const isNajafArrival = arrivalPort.portName.toLowerCase().includes("najaf");
    return isNajafArrival
      ? [najafVenue, karbalaVenue]
      : [karbalaVenue, najafVenue];
  };

  const getCalculationBreakdown = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    venue: any,
    index: number,
    type: "checkin" | "checkout",
  ) => {
    if (!venue.venueId) return null;
    const venueId = venue.venueId;
    const venueName =
      venuesFromApi.find((v) => v.venueId === venueId)?.venueName || "";
    const isLast = index === formData.venues.length - 1;
    let portId: number | undefined;
    if (index === 0) {
      portId = formData.arrivalAirport;
    } else if (isLast) {
      portId = formData.departureAirport;
    } else {
      portId = undefined;
    }
    let intercityTime: string | null = null;
    let intercityUsed = false;
    if (type === "checkout" && !isLast) {
      const nextVenue = formData.venues[index + 1];
      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            parseLocalDate(venue.toDate),
            getTourType(),
          )
        : null;
      if (intercity?.checkoutTime) {
        intercityTime = intercity.checkoutTime;
        intercityUsed = true;
      }
      // MIQAAT fallback (ONLY if no intercity from API)
      if (getTourType() === TourType.MIQAAT && !intercityUsed) {
        intercityTime = venue.toTime;
        intercityUsed = true;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiqaat = getTourType() === TourType.MIQAAT;
    const isIntercityCase = type === "checkout" && !isLast && intercityUsed;
    let hasTravel = false;
    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];
      if (prevVenue?.venueId) {
        const intercity = getIntercityCheckout(
          intercityCheckouts,
          prevVenue.venueId,
          venue.venueId,
          parseLocalDate(prevVenue.toDate),
          getTourType(),
        );
        if (intercity?.travelTime) {
          hasTravel = true;
        }
      }
    }

    const buffersData = getTotalVenueBuffer(
      buffers,
      venueId,
      portId,
      !(isIntercityCase || hasTravel),
    );

    const accommodationBuffers = buffers.filter(
      (b) => b.venueId === venueId && b.type === "1",
    );

    const transportBuffers = !(isIntercityCase || hasTravel)
      ? buffers.filter((b) => {
          if (b.venueId !== venueId || b.type !== "2") return false;
          if (portId != null) {
            const selectedPort = ports.find((p) => p.portId === portId);
            return (
              Number(b.portId) === Number(portId) ||
              !b.portId ||
              (selectedPort &&
                b.portName &&
                selectedPort.portName.toLowerCase().trim() ===
                  b.portName.toLowerCase().trim())
            );
          }

          return true;
        })
      : [];

    const sumBuffers = (
      arr: BufferConfig[],
      key: "checkInBuffer" | "checkOutBuffer",
    ) =>
      arr.reduce(
        (total, b) => total + parseTimeToMinutes(b[key] || "00:00"),
        0,
      );

    const formatHours = (minutes: number) => `${Math.floor(minutes / 60)} hrs`;

    const accommodationMinutes = sumBuffers(
      accommodationBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    const transportMinutes = sumBuffers(
      transportBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    let travelMinutes = 0;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];
      if (prevVenue?.venueId) {
        const intercity =
          prevVenue?.venueId && venue.venueId
            ? getIntercityCheckout(
                intercityCheckouts,
                prevVenue.venueId,
                venue.venueId,
                new Date(prevVenue.toDate),
                getTourType(),
              )
            : null;
        if (intercity?.travelTime) {
          travelMinutes = getTravelTimeMinutes(intercity.travelTime);
        }
      }
    }

    const totalMinutes =
      (isIntercityCase
        ? accommodationMinutes
        : type === "checkin"
          ? buffersData.checkIn
          : buffersData.checkOut) + travelMinutes;

    const rawBaseTime =
      type === "checkin"
        ? index === 0
          ? formData.arrivalTime
          : venue.fromTime
        : isLast
          ? formData.departureTime
          : intercityTime || venue.toTime;

    const baseTime = normalizeTime(rawBaseTime || "");

    return {
      venueName,
      baseTime,
      accommodation: formatHours(accommodationMinutes),
      transport: formatHours(transportMinutes),
      totalBuffer: formatHours(totalMinutes),
      hasTransport: !isIntercityCase && transportMinutes > 0,
      usedIntercity: intercityUsed,
      travel:
        travelMinutes > 0
          ? formatHours(travelMinutes)
          : getTourType() === TourType.MIQAAT
            ? "1 hrs"
            : null,
    };
  };
  const CalculationBox = ({
    data,
    type,
    index,
    isLast,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    type: "checkin" | "checkout";
    index: number;
    isLast: boolean;
  }) => {
    if (!data) return null;

    const isFirst = index === 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiddle = !isFirst && !isLast;
    let label: string | null = null;
    if (type === "checkin") {
      if (isFirst) {
        label = "Arrival Time";
      } else {
        label = null;
      }
    } else {
      // checkout
      if (data.usedIntercity) {
        label = "Intercity Checkout";
      } else if (isLast) {
        label = "Departure Time";
      } else {
        label = null;
      }
    }

    return (
      <div
        className="absolute z-10 left-0 mt-2 w-64 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-gray-700 space-y-1 
          opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg pointer-events-none"
      >
        <div className="font-medium text-blue-700">Calculation Breakdown</div>
        {label && (
          <div>
            {label}: <span className="font-semibold">{data.baseTime}</span>
          </div>
        )}

        <div>
          Accommodation Buffer:{" "}
          <span className="font-semibold">{data.accommodation}</span>
        </div>

        {data.hasTransport && (
          <div>
            Transport Buffer:
            <span>{data.transport}</span>
          </div>
        )}

        {data.travel && (
          <div>
            Travel Time: <span className="font-semibold">{data.travel}</span>
          </div>
        )}

        <div className="pt-1 border-t">
          Total Buffer:{" "}
          <span className="font-semibold text-blue-700">
            {data.totalBuffer}
          </span>
        </div>
      </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapLocalTourToState = (tour: any) => {
    const mappedVenues = (tour.venues || []).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (v: any, i: number) => {
        const matchingVenue = venuesFromApi.find(
          (venue) =>
            venue.publicId === v.venuePublicId ||
            venue.venueName?.trim().toLowerCase() ===
              v.venueName?.trim().toLowerCase(),
        );

        return {
          id: v.publicId || `venue-${i}`,
          venueId: v.venueId ?? matchingVenue?.venueId ?? null,

          venuePublicId: v.venuePublicId ?? matchingVenue?.publicId,

          hotelPropertyId: null,
          hotelName: "",

          fromDate: formatDateSafe(v.checkInDate),
          fromTime: v.checkInTime?.slice(0, 5) || "",

          toDate: formatDateSafe(v.checkOutDate),
          toTime: v.checkOutTime?.slice(0, 5) || "",
        };
      },
    );

    setArrivalConfirmedFromApi(tour.isArrivalConfirmed ?? false);

    setDepartureConfirmedFromApi(tour.isDepartureConfirmed ?? false);

    setArrivalPortName(tour.arrivalPort || "");
    setDeparturePortName(tour.departurePort || "");

    setFormData((prev) => ({
      ...prev,

      arrivalTransportMode: mapTransportModeFromApi(tour.arrivalTransportMode),

      arrivalAirport: 0,

      arrivalDate: tour.arrivalDate?.split("T")[0] || "",

      arrivalTime: tour.arrivalDate?.split("T")[1]?.slice(0, 5) || "",

      arrivalFlightNumber: tour.arrivalFlightNo || "",

      arrivalBookingRef: tour.arrivalBookingRef || "",

      arrivalAirline: tour.arrivalAirline || "",

      arrivalConfirmed: tour.isArrivalConfirmed ?? false,

      departureTransportMode: mapTransportModeFromApi(
        tour.departureTransportMode,
      ),

      departureAirport: 0,

      departureDate: tour.departureDate?.split("T")[0] || "",

      departureTime: tour.departureDate?.split("T")[1]?.slice(0, 5) || "",

      departureFlightNumber: tour.departureFlightNo || "",

      departureBookingRef: tour.departureBookingRef || "",

      departureAirline: tour.departureAirline || "",

      departureConfirmed: tour.isDepartureConfirmed ?? false,

      tourName: tour.tourName || "",

      parentTourId: tour.parentTourId != null ? String(tour.parentTourId) : "",

      noOfApplicants: String(tour.totalApplicants || ""),

      faizAccommodation: tour.faizAccommodation ? "yes" : "no",

      accommodationPreference:
        tour.accommodationPreference === 1 ? "minimum" : "maximum",

      specialAccommodationRequest: tour.specialAccommodationRequest || "",

      venues: mappedVenues,

      tourType: tour.tourType != null ? String(tour.tourType) : "",
    }));

    setAccommodateBy(
      tour.accommodationPreference === 1 ? "minimum" : "maximum",
    );

    setSpecialRoomRequestDetails(tour.specialAccommodationRequest || "");

    setTourStatus(tour.status ?? 5);

    setIsInitialized(true);
  };

  const mapApiToState = (data: WalkInTourDetailsResponse) => {
    setArrivalConfirmedFromApi(data.isArrivalConfirmed ?? false);
    setDepartureConfirmedFromApi(data.isDepartureConfirmed ?? false);
    setArrivalPortName(data.arrivalPortName || "");
    setDeparturePortName(data.departurePortName || "");
    setTourOperatorName(data.tourOperatorName || "");
    const mappedVenues = (data.venueSchedules || []).map((v, i) => {
      const matchingVenue = venuesFromApi.find(
        (venue) => venue.venueId === v.venueId,
      );

      return {
        id: v.publicId || `venue-${i}`,
        venueId: v.venueId,
        venuePublicId: matchingVenue?.publicId,
        venueName: v.venueName || "",
        hotelPropertyId: v.hotelPropertyId ?? null,
        hotelName: v.hotelName ?? "",
        fromDate: formatDateSafe(v.checkInDate),
        fromTime: v.checkInTime || "",
        toDate: formatDateSafe(v.checkOutDate),
        toTime: v.checkOutTime || "",
      };
    });

    setFormData((prev) => ({
      ...prev,

      arrivalTransportMode: mapTransportModeFromApi(data.arrivalTransportMode),

      arrivalAirport: data.arrivalPortId || 0,
      arrivalDate: data.arrivalDate?.split("T")[0] || "",
      arrivalTime: data.arrivalTime || "",
      arrivalFlightNumber: data.arrivalFlightNo || "",
      arrivalBookingRef: data.arrivalBookingRef || "",
      arrivalAirline: data.arrivalAirline || "",
      arrivalConfirmed: data.isArrivalConfirmed ?? false,

      departureTransportMode: mapTransportModeFromApi(
        data.departureTransportMode,
      ),

      departureAirport: data.departurePortId || 0,
      departureDate: data.departureDate?.split("T")[0] || "",
      departureTime: data.departureTime || "",
      departureFlightNumber: data.departureFlightNo || "",
      departureBookingRef: data.departureBookingRef || "",
      departureAirline: data.departureAirline || "",
      departureConfirmed: data.isDepartureConfirmed ?? false,

      tourName: data.tourName || "",
      parentTourId: data.parent_id != null ? String(data.parent_id) : "",
      noOfApplicants: String(data.noOfApplicants || ""),
      faizAccommodation: data.faizAccommodation ? "yes" : "no",

      accommodationPreference:
        data.accommodationPreference === 1 ? "minimum" : "maximum",
      specialAccommodationRequest: data.specialAccommodationRequest || "",
      venues: mappedVenues,
      tourType: "",
    }));

    if (data.faizAccommodation) {
      setAccommodateBy(
        data.accommodationPreference === 1 ? "minimum" : "maximum",
      );
      setSpecialRoomRequestDetails(data.specialAccommodationRequest || "");
    } else {
      setAccommodateBy("maximum");
      setSpecialRoomRequestDetails("");
    }
    setTourStatus(data.status);
    setIsInitialized(true);
  };

  const formatLocalDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // const handleSave = async () => {
  //   try {
  //     setLoading(true);
  //     if (!id) {
  //       toast.error("Missing tour ID");
  //       return;
  //     }

  //     const payload = buildUpdatePayload();

  //     console.log("UPDATE PAYLOAD:", payload);

  //     await updateTour(id, payload);

  //     toast.success("Tour updated successfully!");
  //     navigate(`/tours?bookingFilter=${previousBookingFilter}`, {
  //       state: { refresh: true },
  //     });
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to update tour");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Group applicants by family for HOF clustering
  const getGroupedApplicantsByFamily = () => {
    const grouped: Record<string, typeof shortlistedApplicantList> = {};

    shortlistedApplicantList.forEach((applicant) => {
      const hof = applicant.hofITS?.trim();

      if (!hof) {
        if (!grouped["no-family"]) {
          grouped["no-family"] = [];
        }
        grouped["no-family"].push(applicant);
        return;
      }

      if (!grouped[hof]) {
        grouped[hof] = [];
      }

      grouped[hof].push(applicant);
    });

    return grouped;
  };

  //   const getRoomPreferenceForApplicant = (applicantId: string) => {
  //     const applicant = approvedApplicants.find((a) => a.id === applicantId);
  //     if (!applicant) return "Private";
  //     return applicant.gender === "Male" ? "Shared-Gents" : "Shared-Ladies";
  //   };

  const isPastCutoff = (() => {
    if (!formData.arrivalDate || !formData.departureDate) {
      return false;
    }
    const today = new Date();
    const arrivalDate = new Date(formData.arrivalDate);
    const departureDate = new Date(formData.departureDate);
    return today > arrivalDate && today > departureDate;
  })();

  const enabledTabs = [
    "add-pax",
    "arrival-departure",
    "venue-summary",

    ...(!isExternalTour ? ["accommodation", "room-allocation"] : []),

    "services",
    "itenary",
    "overview",
  ];

  const latestReviewMessage = reviewConversation[reviewConversation.length - 1];
  const hasNewAdminMessage = latestReviewMessage?.sender === "backoffice";

  const renderTourOperatorRequestAccordion = () => (
    <Accordion type="multiple" defaultValue={[]} className="w-full">
      <AccordionItem
        value="tour-operator-request"
        className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm"
      >
        <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
          <div
            className="flex w-full flex-col gap-3 pr-3 text-left sm:flex-row sm:items-start sm:justify-between"
            style={{ alignItems: "center" }}
          >
            <div className="flex items-start gap-3">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-[#5B9BD5] shadow-sm">
                <MessageSquareText className="h-5 w-5" />
                {hasNewAdminMessage && (
                  <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-red-500" />
                )}
              </span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Tour Operator Request
                </h2>
                <p className="mt-1 text-xs font-normal text-gray-600">
                  Conversation between tour operator and admin.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {hasNewAdminMessage && (
                <Badge className="border-red-300 bg-red-100 text-red-700 hover:bg-red-100">
                  New message
                </Badge>
              )}
              <Badge className="border-[#5B9BD5]/30 bg-white text-[#5B9BD5] hover:bg-white">
                {reviewConversation.length} messages
              </Badge>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 pt-0">
          <div className="space-y-3">
            {reviewConversation.length > 0 ? (
              <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/40 p-3">
                <div className="max-h-[360px] space-y-2 overflow-y-auto pb-2 pr-1">
                  {reviewConversation.map((msg, index) => {
                    const isLatest = index === reviewConversation.length - 1;
                    const isAdmin = msg.sender === "backoffice";

                    return (
                      <div
                        key={msg.id}
                        className={`flex px-0.5 ${
                          isAdmin ? "justify-start" : "justify-end"
                        }`}
                      >
                        <div
                          className={`max-w-[78%] rounded-2xl border px-3 py-2 shadow-sm ${
                            isAdmin
                              ? "rounded-bl-sm border-blue-200 bg-white"
                              : "rounded-br-sm border-green-200 bg-green-50"
                          } ${isLatest ? "ring-1 ring-red-300" : ""}`}
                        >
                          <div className="mb-1 flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-1.5">
                              <span className="truncate text-[11px] font-bold text-gray-900">
                                {msg.senderName}
                              </span>
                              <Badge
                                className={
                                  isAdmin
                                    ? "border-blue-300 bg-blue-100 px-1.5 py-0 text-[9px] text-blue-700"
                                    : "border-green-300 bg-green-100 px-1.5 py-0 text-[9px] text-green-700"
                                }
                              >
                                {isAdmin ? "Admin" : "TO"}
                              </Badge>
                              {isLatest && (
                                <Badge className="border-red-300 bg-red-100 px-1.5 py-0 text-[9px] text-red-700">
                                  Latest
                                </Badge>
                              )}
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-[10px] font-semibold text-gray-500">
                                {msg.timestamp}
                              </p>
                              <p className="text-[9px] text-gray-400">
                                {msg.dateTime}
                              </p>
                            </div>
                          </div>
                          <p className="whitespace-pre-line text-xs leading-relaxed text-gray-800">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-blue-50 p-4">
                <p className="text-center text-xs text-gray-600 md:text-sm">
                  No conversation messages yet.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-2">
              <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                <textarea
                  value={responseToAdmin}
                  onChange={(e) => setResponseToAdmin(e.target.value)}
                  placeholder="Type response to admin..."
                  className="h-10 min-h-10 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#5B9BD5]"
                />
                <Button
                  size="icon"
                  className="h-10 w-10 text-white"
                  style={{ backgroundColor: "#5B9BD5" }}
                  onClick={handleSendReviewResponse}
                  disabled={!responseToAdmin.trim()}
                  title="Send Response"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  const mapVisaApplicationToApplicant = useCallback((visa: VisaApplication): Applicant => ({
    id: String(visa.visaApplicationId),

    tourOperatorId: visa.tourOperatorId,
    ejamaatId: visa.ejamaatId ?? "",
    itsNo: visa.itsNo ?? "",
    referenceId: visa.referenceId ?? null,
    isNonItsUser: Boolean(visa.isNonItsUser),

    applicantType: visa.applicantType as Applicant["applicantType"],
    category: visa.category ?? "",

    guardianIts: visa.guardianITS ?? "",
    hofIts: visa.hofITS ?? "",

    title: visa.title ?? "",
    firstName: visa.firstName ?? "",
    middleName: visa.middleName ?? "",
    lastName: visa.lastName ?? "",
    fullName: [visa.title, visa.firstName, visa.middleName, visa.lastName]
      .filter(Boolean)
      .join(" "),

    dob: visa.dateOfBirth?.split("T")[0] ?? "",
    age: String(visa.age ?? 0),
    gender: visa.gender ?? "",
    birthPlace: visa.birthPlace ?? "",
    nationality: visa.nationality ?? "",

    profession: visa.profession ?? "",

    passportNo: visa.passportNo ?? "",
    passportType: visa.passportType ?? "",
    passportFirstName: visa.passportFirstName ?? "",
    passportMiddleName: visa.passportMiddleName ?? "",
    passportLastName: visa.passportLastName ?? "",
    passportArabicName: visa.passportArabicName ?? "",

    passportDob: visa.passportDOB?.split("T")[0] ?? "",
    passportGender: visa.passportGender ?? "",
    passportNationality: visa.passportNationality ?? "",

    passportIssueDate: visa.passportIssueDate?.split("T")[0] ?? "",
    passportExpiryDate: visa.passportExpiryDate?.split("T")[0] ?? "",
    passportIssuePlace: visa.passportIssuePlace ?? "",

    fatherName: visa.fatherName ?? "",
    fatherNameArabic: visa.fatherNameArabic ?? "",
    fatherItsNo: visa.fatherITSNo ?? "",

    grandfatherName: visa.grandfatherName ?? "",
    grandfatherNameArabic: visa.grandfatherNameArabic ?? "",
    grandfatherItsNo: visa.grandfatherITSNo ?? "",

    motherName: visa.motherName ?? "",
    motherNameArabic: visa.motherNameArabic ?? "",
    motherItsNo: visa.motherITSNo ?? "",

    mothersFatherName: visa.mothersFatherName ?? "",
    mothersFatherNameArabic: visa.mothersFatherNameArabic ?? "",
    mothersFatherItsNo: visa.mothersFatherITSNo ?? "",

    visaRequired: visa.visaRequired,
    visaNotRequiredRemark: visa.visaNotRequiredRemark ?? "",

    documents: [],

    remarks: visa.remarks ?? "",
    isExpanded: false,
    isFamilyOpen: false,

    validationErrors: [],
    showValidationErrors: false,
    isMiqaatApplicant: visa.isMiqaatApplicant,
  }), []);

  const mapShortlistedVisaApplicationToApplicant = (
    visa: ShortlistedVisaApplication,
  ): Applicant => ({
    id: String(visa.visaApplicationId),

    tourOperatorId: visa.tourOperatorId,

    ejamaatId: visa.ejamaatId ?? "",
    itsNo: visa.itsNo ?? "",
    isNonItsUser: Boolean(visa.isNonItsUser),

    applicantType: visa.applicantType as Applicant["applicantType"],
    category: visa.category ?? "",

    guardianIts: visa.guardianITS ?? "",
    hofIts: visa.hofITS ?? "",

    title: visa.title ?? "",
    firstName: visa.firstName ?? "",
    middleName: visa.middleName ?? "",
    lastName: visa.lastName ?? "",

    fullName: [visa.title, visa.firstName, visa.middleName, visa.lastName]
      .filter(Boolean)
      .join(" "),

    dob: visa.dateOfBirth?.split("T")[0] ?? "",
    age: String(visa.age ?? 0),
    gender: visa.gender ?? "",
    birthPlace: visa.birthPlace ?? "",
    nationality: visa.nationality ?? "",

    profession: visa.profession ?? "",

    passportNo: visa.passportNo ?? "",
    passportType: visa.passportType ?? "",
    passportFirstName: visa.passportFirstName ?? "",
    passportMiddleName: visa.passportMiddleName ?? "",
    passportLastName: visa.passportLastName ?? "",
    passportArabicName: visa.passportArabicName ?? "",

    passportDob: visa.passportDOB?.split("T")[0] ?? "",
    passportGender: visa.passportGender ?? "",
    passportNationality: visa.passportNationality ?? "",

    passportIssueDate: visa.passportIssueDate?.split("T")[0] ?? "",
    passportExpiryDate: visa.passportExpiryDate?.split("T")[0] ?? "",
    passportIssuePlace: visa.passportIssuePlace ?? "",

    fatherName: visa.fatherName ?? "",
    fatherNameArabic: visa.fatherNameArabic ?? "",
    fatherItsNo: visa.fatherITSNo ?? "",

    grandfatherName: visa.grandfatherName ?? "",
    grandfatherNameArabic: visa.grandfatherNameArabic ?? "",
    grandfatherItsNo: visa.grandfatherITSNo ?? "",

    motherName: visa.motherName ?? "",
    motherNameArabic: visa.motherNameArabic ?? "",
    motherItsNo: visa.motherITSNo ?? "",

    mothersFatherName: visa.mothersFatherName ?? "",
    mothersFatherNameArabic: visa.mothersFatherNameArabic ?? "",
    mothersFatherItsNo: visa.mothersFatherITSNo ?? "",

    visaRequired: visa.visaRequired,
    visaNotRequiredRemark: visa.visaNotRequiredRemark ?? "",

    documents: [],

    remarks: visa.remarks ?? "",

    isExpanded: false,
    isFamilyOpen: false,

    validationErrors: [],
    showValidationErrors: false,

    isMiqaatApplicant: visa.isMiqaatApplicant,
  });

  useEffect(() => {
    if (!id || !showSelectApplicantsDialog) return;
    let cancelled = false;

    const loadAvailableVisaApplicants = async () => {
      setLoadingAvailableApplicants(true);
      try {
        const response = await getVisaApplicants(id, {
          searchText: debouncedApplicantFilters.searchText?.trim() || undefined,
        });
        if (!cancelled) {
          setAvailableVisaApplicants(response.data.map(mapVisaApplicationToApplicant));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load tour visa applicants", error);
          toast.error("Failed to load applicants");
          setAvailableVisaApplicants([]);
        }
      } finally {
        if (!cancelled) setLoadingAvailableApplicants(false);
      }
    };

    void loadAvailableVisaApplicants();
    return () => { cancelled = true; };
  }, [id, showSelectApplicantsDialog, debouncedApplicantFilters, mapVisaApplicationToApplicant]);

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-end mb-3">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {tourStatus !== null && (
              <Badge
                className={`${statusMeta.className} mb-3 w-fit px-3 py-1 text-xs sm:mb-0 sm:text-sm`}
              >
                {statusName}
              </Badge>
            )}

            {tourData?.tourCode && (
              <Badge className="bg-purple-100 text-purple-700 border-purple-300 px-3 py-1 text-xs sm:text-sm">
                {tourData.tourCode}
              </Badge>
            )}
          </div>
        </div>

        <div>
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-0 mb-4 border-b border-border">
            {[
              { id: "add-pax", label: "Add Pax", icon: UserPlus },
              {
                id: "arrival-departure",
                label: "Arrival & Departure",
                icon: Plane,
              },
              { id: "venue-summary", label: "Venue Summary", icon: FileText },
              // Hide Accommodation and Room Summary for External tours
              ...(!isExternalTour
                ? [
                    {
                      id: "accommodation",
                      label: "Accommodation",
                      icon: Building,
                    },
                    {
                      id: "room-allocation",
                      label: "Room Summary",
                      icon: Users,
                    },
                  ]
                : []),
              { id: "services", label: "Services", icon: Settings },
              { id: "itenary", label: "Itenary", icon: MapPin },
              { id: "overview", label: "Overview", icon: FileText },
            ].map(({ id, label, icon: Icon }, index) => (
              <button
                key={id}
                disabled={!enabledTabs.includes(id)}
                onClick={() => {
                  if (enabledTabs.includes(id)) {
                    setActiveTab(id as typeof activeTab);
                  }
                }}
                className={`flex items-center gap-2 px-5 py-3 relative text-sm
                  ${
                    !enabledTabs.includes(id)
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : activeTab === id
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs ${
                    activeTab === id
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {index + 1}
                </div>
                <span className="font-medium">{label}</span>
                {activeTab === id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="pb-8">
            {/* Step 1: Add Pax */}
            {activeTab === "add-pax" && (
              <div className="space-y-4 overflow-x-hidden rounded-2xl bg-gradient-to-br from-sky-50/80 via-white to-blue-50/60 p-3 sm:p-4">
                <Card className="gap-0 border-sky-200/70 bg-white/90 shadow-sm">
                  <CardContent className="p-3 sm:p-4">
                    <div
                      className="flex items-end justify-between gap-4"
                      style={{ alignItems: "center" }}
                    >
                      {/* Tour Operator */}
                      <div className="w-full max-w-md">
                        <SelectFieldset label="Tour Operator" required>
                          <Select value={tourOperatorName} disabled>
                            <SelectTriggerUI className="border-gray-300 mt-1.5">
                              <SelectValue placeholder="Select Tour Operator">
                                {tourOperatorName || "Select Tour Operator"}
                              </SelectValue>
                            </SelectTriggerUI>
                          </Select>
                        </SelectFieldset>
                      </div>

                      {/* Existing Add Applicants button */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setAvailableVisaApplicants([]);
                            setLoadingAvailableApplicants(true);
                            setShowSelectApplicantsDialog(true);
                          }}
                          className="h-9 min-w-36 px-3 shrink-0 bg-sky-600 text-white hover:bg-sky-700"
                        >
                          <Users className="mr-1.5 h-4 w-4" />
                          Select Applicants
                        </Button>

                        <Button
                          size="sm"
                          onClick={() => setShowApplicantDialog(true)}
                          className="h-9 min-w-36 px-3 shrink-0 bg-sky-600 text-white hover:bg-sky-700"
                        >
                          <UserPlus className="mr-1.5 h-4 w-4" />
                          Add Applicants
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <ApplicantTable
                  applicants={applicants}
                  onRemove={(applicantId) => {
                    const applicant = applicants.find(
                      (item) => item.id === applicantId,
                    );

                    if (!applicant) return;

                    setApplicantToDelete(applicant);
                    setShowDeleteDialog(true);
                  }}
                />
              </div>
            )}

            {/* Tab 2: Arrival & Departure */}
            {activeTab === "arrival-departure" && (
              <div className="space-y-5">
                {/* Arrival and Departure Cards Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3">
                  {/* Arrival Card */}
                  <Card
                    className="bg-white border-2 border-primary/40 shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="bg-card" style={{ gap: "0px" }}>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2 items-center">
                          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                            <ArrowRight className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <CardTitle className="text-base font-semibold text-foreground">
                            Arrival Details
                          </CardTitle>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          <CardTitle className="text-sm font-semibold block text-foreground">
                            Transport Mode
                          </CardTitle>
                          <Badge
                            className={
                              formData.arrivalTransportMode === "flight"
                                ? "bg-sky-100 text-sky-700 border-sky-300 px-2 py-1 text-xs sm:text-sm"
                                : formData.arrivalTransportMode === "road"
                                  ? "bg-amber-100 text-amber-700 border-amber-300 px-3 py-1 text-xs sm:text-sm"
                                  : "bg-emerald-100 text-emerald-700 border-emerald-300 px-3 py-1 text-xs sm:text-sm"
                            }
                          >
                            {formData.arrivalTransportMode === "flight"
                              ? "By Flight"
                              : formData.arrivalTransportMode === "road"
                                ? "By Road"
                                : "By Sea"}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4 bg-card">
                      {formData.arrivalTransportMode === "flight" ? (
                        <>
                          {/* Row 1 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.arrivalDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  isDisabled={true}
                                  onChange={(date) => {
                                    const formatted = formatLocalDate(date);

                                    setIsVenueManuallyChanged(true);

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalDate: formatted,
                                    }));
                                  }}
                                />
                              </SelectFieldset>
                            </div>
                            <div>
                              <InputFieldset
                                label="Flight No."
                                required
                                placeholder="IQ2021"
                                className="text-xs text-gray-400"
                                value={formData.arrivalFlightNumber}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalFlightNumber: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>
                          </div>

                          {/* Row 2 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="time"
                                className="text-xs text-gray-400"
                                value={formData.arrivalTime}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalTime: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>
                            <InputFieldset
                              label="Airport"
                              className="text-xs text-gray-400"
                              value={arrivalPortName}
                              disabled
                            />
                          </div>

                          {/* Row 3 */}
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                            <div>
                              <InputFieldset
                                label="Airline"
                                placeholder="Iraqi Airways"
                                className="text-xs text-gray-400"
                                value={formData.arrivalAirline}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalAirline: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>

                            <div>
                              <InputFieldset
                                label="Booking Ref"
                                placeholder="KUN-ARR"
                                className="text-xs text-gray-400"
                                value={formData.arrivalBookingRef}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalBookingRef: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>

                            <div className="pb-0.5">
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                              <div className="flex items-center h-9">
                                {arrivalConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.arrivalConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalConfirmed: checked,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.arrivalDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  isDisabled={true}
                                  onChange={(date) => {
                                    const formatted = formatLocalDate(date);

                                    setIsVenueManuallyChanged(true);

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalDate: formatted,
                                    }));
                                  }}
                                />
                              </SelectFieldset>
                            </div>
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="time"
                                className="text-xs text-gray-400"
                                value={formData.arrivalTime}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalTime: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            <div>
                              <InputFieldset
                                label="Airport"
                                className="text-xs text-gray-400"
                                value={arrivalPortName}
                                disabled
                              />
                            </div>
                            <div className="flex items-center gap-2 mt-5">
                              <div className="flex items-center h-9">
                                {arrivalConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.arrivalConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalConfirmed: checked,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Departure Card */}
                  <Card
                    className="bg-white border-2 border-destructive/40 shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="bg-card" style={{ gap: "0px" }}>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2 items-center">
                          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                            <Plane className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <CardTitle className="text-base font-semibold text-foreground">
                            Departure Details
                          </CardTitle>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          <CardTitle className="text-sm font-semibold block text-foreground">
                            Transport Mode
                          </CardTitle>
                          <Badge
                            className={
                              formData.departureTransportMode === "flight"
                                ? "bg-sky-100 text-sky-700 border-sky-300 px-2 py-1 text-xs sm:text-sm"
                                : formData.departureTransportMode === "road"
                                  ? "bg-amber-100 text-amber-700 border-amber-300 px-3 py-1 text-xs sm:text-sm"
                                  : "bg-emerald-100 text-emerald-700 border-emerald-300 px-3 py-1 text-xs sm:text-sm"
                            }
                          >
                            {formData.departureTransportMode === "flight"
                              ? "By Flight"
                              : formData.departureTransportMode === "road"
                                ? "By Road"
                                : "By Sea"}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4 bg-card">
                      {formData.departureTransportMode === "flight" ? (
                        <>
                          {/* Row 1 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.departureDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  // existing validation logic

                                  isDisabled={true}
                                  onChange={(date: Date) => {
                                    const formatted = formatLocalDate(date);
                                    setIsVenueManuallyChanged(true);
                                    setFormData((prev) => ({
                                      ...prev,
                                      departureDate: formatted,
                                    }));
                                  }}
                                />
                              </SelectFieldset>
                            </div>
                            <div>
                              <InputFieldset
                                label="Flight No."
                                required
                                placeholder="IQ2022"
                                className="text-xs text-gray-400"
                                value={formData.departureFlightNumber}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureFlightNumber: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>
                          </div>

                          {/* Row 2 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="time"
                                className="text-xs text-gray-400"
                                value={formData.departureTime}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureTime: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>
                            <InputFieldset
                              label="Airport"
                              className="text-xs text-gray-400"
                              value={departurePortName}
                              disabled
                            />
                          </div>

                          {/* Row 3 */}
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                            <div>
                              <InputFieldset
                                label="Airline"
                                placeholder="Iraqi Airways"
                                className="text-xs text-gray-400"
                                value={formData.departureAirline}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureAirline: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>

                            <div>
                              <InputFieldset
                                label="Booking Ref"
                                placeholder="KUN-DEP"
                                className="text-xs text-gray-400"
                                value={formData.departureBookingRef}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureBookingRef: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>

                            <div className="pb-0.5">
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                              <div className="flex items-center h-9">
                                {departureConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.departureConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        departureConfirmed: checked,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.departureDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  isDisabled={true}
                                  onChange={(date: Date) => {
                                    const formatted = formatLocalDate(date);
                                    setIsVenueManuallyChanged(true);
                                    setFormData((prev) => ({
                                      ...prev,
                                      departureDate: formatted,
                                    }));
                                    setRefreshKey((k) => k + 1);
                                  }}
                                />
                              </SelectFieldset>
                            </div>
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="time"
                                className="text-xs text-gray-400"
                                value={formData.departureTime}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    departureTime: e.target.value,
                                  }))
                                }
                                disabled={true}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            <InputFieldset
                              label="Airport"
                              className="text-xs text-gray-400"
                              value={departurePortName}
                              disabled
                            />
                            <div className="flex items-center gap-2 mt-5">
                              <div className="flex items-center h-9">
                                {departureConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.departureConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        departureConfirmed: checked,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Tab 3: Venue Summary */}
            {activeTab === "venue-summary" && (
              <div className="space-y-5">
                <Card
                  className="bg-white border border-border shadow-sm"
                  style={{ gap: "0px" }}
                >
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold">
                      Venue Summary
                    </CardTitle>
                  </CardHeader>

                  <CardContent style={{ padding: "0px 24px 20px 24px" }}>
                    <div
                      className="grid grid-cols-1 md:grid-cols-12 gap-3 border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                      style={{ paddingLeft: "16px", paddingRight: "16px" }}
                    >
                      {/* Tour Name */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="Tour Name"
                          required
                          placeholder="Enter tour name"
                          className="text-sm focus:ring-2 focus:ring-primary/30 text-gray-400"
                          value={formData.tourName}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              tourName: e.target.value,
                            }))
                          }
                          disabled={true}
                        />
                        {errors.tourName && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.tourName}
                          </p>
                        )}
                      </div>

                      {/* Applicants */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="No. of Applicants"
                          required
                          type="number"
                          placeholder="0"
                          className="text-sm focus:ring-2 focus:ring-primary/30 text-gray-400"
                          value={formData.noOfApplicants}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              noOfApplicants: e.target.value,
                            }))
                          }
                          disabled={true}
                        />
                        {errors.noOfApplicants && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.noOfApplicants}
                          </p>
                        )}
                      </div>

                      {/* Parent Tour Id */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="Parent Tour Id"
                          placeholder="Enter Parent Tour Id"
                          className="text-sm"
                          maxLength={50}
                          value={formData.parentTourId}
                          disabled={true}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();

                            // Allow only letters, numbers and hyphen
                            if (!/^[A-Z0-9-]*$/.test(value)) {
                              return;
                            }

                            if (value.length > 50) {
                              return;
                            }

                            setFormData((prev) => ({
                              ...prev,
                              parentTourId: value,
                            }));
                          }}
                        />

                        {errors.parentTourId && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.parentTourId}
                          </p>
                        )}
                      </div>

                      {/* Faiz */}
                      {tourData?.tourCategory !== 1 && (
                        <div className="md:col-span-3 space-y-2">
                          <Label className="text-sm font-medium text-muted-foreground">
                            Faiz Accommodation{" "}
                            <span className="text-red-500">*</span>
                          </Label>
                          <RadioGroup
                            value={formData.faizAccommodation}
                            disabled
                            className="flex gap-6 items-center h-10"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem
                                value="yes"
                                id="faiz-yes"
                                className="
                                            h-5 w-5 rounded-full
                                            border-2 border-gray-200
                                            flex items-center justify-center
                                            data-[state=checked]:border-primary
                                          "
                              >
                                <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                              </RadioGroupItem>
                              <Label
                                htmlFor="faiz-yes"
                                className="text-sm cursor-pointer"
                              >
                                Yes
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem
                                value="no"
                                id="faiz-no"
                                className="
                                            h-5 w-5 rounded-full
                                            border-2 border-gray-200
                                            flex items-center justify-center
                                            data-[state=checked]:border-primary
                                          "
                              >
                                <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                              </RadioGroupItem>
                              <Label
                                htmlFor="faiz-no"
                                className="text-sm cursor-pointer"
                              >
                                No
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                    </div>

                    {tourData?.tourCategory !== 1 &&
                      formData.faizAccommodation === "yes" && (
                        <Card className="bg-white border-blue-200 bg-blue-50/50 mt-6 mb-6">
                          <CardContent style={{ padding: "16px" }}>
                            {/* Occupancy Question */}
                            <div className="mb-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                              <div className="lg:col-span-6">
                                <Label className="text-sm font-medium text-gray-900 block">
                                  Want to accommodate based on Min or Max
                                  Occupancy?
                                </Label>
                              </div>

                              <div className="lg:col-span-6">
                                <RadioGroup
                                  value={accommodateBy || ""}
                                  onValueChange={(v) =>
                                    setAccommodateBy(v as "minimum" | "maximum")
                                  }
                                  className="flex gap-6 items-center"
                                >
                                  {/* Minimum */}
                                  <div className="flex items-center gap-2">
                                    <RadioGroupItem
                                      value="minimum"
                                      id="min-occupancy"
                                      className="
                                        h-5 w-5 rounded-full
                                        border-2 border-gray-200
                                        flex items-center justify-center
                                        data-[state=checked]:border-primary
                                      "
                                    >
                                      <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                    </RadioGroupItem>

                                    <Label
                                      htmlFor="min-occupancy"
                                      className="text-sm cursor-pointer"
                                    >
                                      Min Occupancy
                                    </Label>
                                  </div>

                                  {/* Maximum */}
                                  <div className="flex items-center gap-2">
                                    <RadioGroupItem
                                      value="maximum"
                                      id="max-occupancy"
                                      className="
                                        h-5 w-5 rounded-full
                                        border-2 border-gray-200
                                        flex items-center justify-center
                                        data-[state=checked]:border-primary
                                      "
                                    >
                                      <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                    </RadioGroupItem>

                                    <Label
                                      htmlFor="max-occupancy"
                                      className="text-sm cursor-pointer"
                                    >
                                      Max Occupancy
                                    </Label>
                                  </div>
                                </RadioGroup>
                              </div>
                            </div>
                            <InputFieldset
                              label="Any special accommodation request?"
                              placeholder="e.g. Wheelchair accessible, Ground floor, Adjacent rooms..."
                              className="text-sm focus:ring-2 focus:ring-primary/30 text-gray-400"
                              value={specialRoomRequestDetails}
                              disabled={true}
                              onChange={(e) =>
                                setSpecialRoomRequestDetails(e.target.value)
                              }
                            />
                          </CardContent>
                        </Card>
                      )}

                    {!isInstantTour && (
                      <div className="mt-4">
                        {renderTourOperatorRequestAccordion()}
                      </div>
                    )}

                    {/* ================= VENUE SECTION ================= */}
                    <div className="space-y-4" style={{ marginTop: "14px" }}>
                      {/* Section Header */}
                      <div
                        className="flex"
                        style={{
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            Venue Schedule
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            Add venues with check-in and check-out details
                          </p>
                        </div>
                      </div>

                      {/* Empty State */}
                      {formData.venues.length === 0 ? (
                        <div className="border border-dashed border-border rounded-lg p-10 text-center">
                          <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">
                            No venues added yet
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.venues.map((venue, index) => {
                            const isFirst = index === 0;
                            const isLast = index === formData.venues.length - 1;
                            return (
                              <div
                                key={venue.id}
                                className="border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                              >
                                <div className="flex gap-4">
                                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 space-y-4">
                                    <div>
                                      {/* Venue Name */}
                                      <div
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                                        style={{ alignItems: "center" }}
                                      >
                                        <div className="space-y-1">
                                          <SelectFieldset
                                            label={`Venue Name ${index + 1}`}
                                            required
                                            error={errors[`venueName-${index}`]}
                                          >
                                            <Select
                                              value={
                                                venue.venueId
                                                  ? venue.venueId.toString()
                                                  : ""
                                              }
                                              disabled={true}
                                              onValueChange={(value) => {
                                                const updated = [
                                                  ...formData.venues,
                                                ];
                                                updated[index].venueId =
                                                  Number(value);
                                                setIsVenueManuallyChanged(
                                                  false,
                                                );

                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: prev.venues.map(
                                                    (v, i) =>
                                                      i === index
                                                        ? {
                                                            ...v,
                                                            venueId:
                                                              Number(value),
                                                          }
                                                        : v,
                                                  ),
                                                }));
                                              }}
                                            >
                                              <SelectTriggerUI className="text-sm">
                                                <SelectValue placeholder="Select venue">
                                                  {venue.venueId
                                                    ? venuesFromApi.find(
                                                        (v) =>
                                                          v.venueId ===
                                                          venue.venueId,
                                                      )?.venueName
                                                    : "Select venue"}
                                                </SelectValue>
                                              </SelectTriggerUI>

                                              <SelectContent>
                                                {venuesFromApi.map((v) => (
                                                  <SelectItem
                                                    key={v.venueId}
                                                    value={v.venueId.toString()}
                                                  >
                                                    {v.venueName}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </SelectFieldset>
                                        </div>
                                        {errors[`venueName-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`venueName-${index}`]}
                                          </p>
                                        )}
                                        <div className="space-y-1">
                                          {formData.faizAccommodation ===
                                            "no" && (
                                            <SelectFieldset
                                              label={`Hotel Name ${index + 1}`}
                                            >
                                              <Select
                                                value={
                                                  venue.hotelPropertyId?.toString() ??
                                                  ""
                                                }
                                                onValueChange={(value) => {
                                                  const selectedHotel =
                                                    hotelsByVenue[
                                                      venue.venueId ?? 0
                                                    ]?.find(
                                                      (h) =>
                                                        h.propertyId ===
                                                        Number(value),
                                                    );

                                                  const updated = [
                                                    ...formData.venues,
                                                  ];

                                                  updated[
                                                    index
                                                  ].hotelPropertyId =
                                                    Number(value);
                                                  updated[index].hotelName =
                                                    selectedHotel?.propertyName ??
                                                    "";

                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    venues: updated,
                                                  }));
                                                }}
                                              >
                                                <SelectTriggerUI className="h-10 text-sm">
                                                  <SelectValue placeholder="Select Hotel" />
                                                </SelectTriggerUI>

                                                <SelectContent>
                                                  {(
                                                    hotelsByVenue[
                                                      venue.venueId ?? 0
                                                    ] ?? []
                                                  ).map((hotel) => (
                                                    <SelectItem
                                                      key={hotel.propertyId}
                                                      value={hotel.propertyId.toString()}
                                                    >
                                                      {hotel.propertyName}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </SelectFieldset>
                                          )}
                                        </div>
                                        <div className="space-y-1"></div>
                                        <div
                                          className="space-y-1"
                                          style={{
                                            display: "flex",
                                            justifyContent: "end",
                                          }}
                                        >
                                          {/* Delete */}
                                          {!(isFirst || isLast) && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                              onClick={() => {
                                                const updated =
                                                  formData.venues.filter(
                                                    (_, i) => i !== index,
                                                  );

                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: updated,
                                                }));
                                              }}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                      {/* Check-In Date */}
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label="Check-In Date"
                                          required
                                          error={errors[`fromDate-${index}`]}
                                        >
                                          <CustomDayPicker
                                            value={venue.fromDate}
                                            operatorExpiryDate={
                                              operatorExpiryDate ?? undefined
                                            }
                                            mode="range"
                                            highlightRange={{
                                              from:
                                                formData.arrivalDate ||
                                                undefined,
                                              to:
                                                formData.departureDate ||
                                                undefined,
                                            }}
                                            isDisabled={true}
                                            onChange={(date: Date) => {
                                              if (isFirst) return;

                                              const formatted =
                                                formatLocalDate(date);

                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].fromDate =
                                                formatted;

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                            placeholder="dd-MM-yyyy"
                                          />
                                        </SelectFieldset>

                                        {errors[`fromDate-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`fromDate-${index}`]}
                                          </p>
                                        )}
                                      </div>

                                      {/* Check-In Time */}
                                      <div className="space-y-1">
                                        <div className="relative group">
                                          <InputFieldset
                                            label="Check-In Time"
                                            required
                                            type="time"
                                            className={`text-sm pr-8 border border-gray-300 rounded-md transition-all outline-none text-gray-400${
                                              isFirst
                                                ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                : ""
                                            }`}
                                            value={venue.fromTime}
                                            disabled
                                            onChange={(e) => {
                                              if (isFirst) return;

                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].fromTime =
                                                e.target.value;

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                          />
                                          <CalculationBox
                                            type="checkin"
                                            index={index}
                                            isLast={
                                              index ===
                                              formData.venues.length - 1
                                            }
                                            data={getCalculationBreakdown(
                                              venue,
                                              index,
                                              "checkin",
                                            )}
                                          />
                                        </div>
                                        {errors[`fromTime-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`fromTime-${index}`]}
                                          </p>
                                        )}
                                      </div>

                                      {/* Check-Out Date */}
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label="Check-Out Date"
                                          required
                                          error={errors[`toDate-${index}`]}
                                        >
                                          <CustomDayPicker
                                            value={venue.toDate}
                                            operatorExpiryDate={
                                              operatorExpiryDate ?? undefined
                                            }
                                            mode="range"
                                            highlightRange={{
                                              from:
                                                formData.arrivalDate ||
                                                undefined,
                                              to:
                                                formData.departureDate ||
                                                undefined,
                                            }}
                                            isDisabled={true}
                                            onChange={(date: Date) => {
                                              const formatted =
                                                formatLocalDate(date);

                                              setIsVenueManuallyChanged(true);

                                              setFormData((prev) => {
                                                const updated = [
                                                  ...prev.venues,
                                                ];
                                                updated[index].toDate =
                                                  formatted;
                                                if (
                                                  index <
                                                  updated.length - 1
                                                ) {
                                                  updated[index + 1].fromDate =
                                                    formatted;
                                                }

                                                return {
                                                  ...prev,
                                                  venues: updated,
                                                };
                                              });
                                            }}
                                            placeholder="dd-MM-yyyy"
                                          />
                                        </SelectFieldset>
                                        {errors[`toDate-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`toDate-${index}`]}
                                          </p>
                                        )}
                                      </div>

                                      {/* Check-Out Time */}
                                      <div className="space-y-1">
                                        <div className="relative group">
                                          <InputFieldset
                                            label="Check-Out Time"
                                            required
                                            type="time"
                                            className={`text-sm border border-gray-300 rounded-md transition-all outline-none text-gray-400${
                                              isLast
                                                ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                : ""
                                            }`}
                                            value={venue.toTime}
                                            disabled
                                            onChange={(e) => {
                                              if (isLast) return;

                                              setFormData((prev) => {
                                                const updated = [
                                                  ...prev.venues,
                                                ];

                                                updated[index].toTime =
                                                  e.target.value;
                                                if (
                                                  index <
                                                  updated.length - 1
                                                ) {
                                                  updated[index + 1].fromTime =
                                                    e.target.value;
                                                }

                                                return {
                                                  ...prev,
                                                  venues: updated,
                                                };
                                              });
                                            }}
                                          />
                                          <CalculationBox
                                            type="checkout"
                                            index={index}
                                            isLast={
                                              index ===
                                              formData.venues.length - 1
                                            }
                                            data={getCalculationBreakdown(
                                              venue,
                                              index,
                                              "checkout",
                                            )}
                                          />
                                        </div>
                                        {errors[`toTime-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`toTime-${index}`]}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Tab 4: Accommodation */}
            {!isExternalTour && activeTab === "accommodation" && (
              <FaizAccommodation
                onRoomsChange={handleFaizRoomsChange}
                venueSummary={formData.venues}
                venueApiOptions={venuesFromApi}
                tourPublicId={id ?? ""}
              />
            )}

            {/* Tab 5: Room Summary */}
            {!isExternalTour && activeTab === "room-allocation" && (
              <RoomSummary
                rooms={rooms}
                setRooms={setRooms}
                approvedApplicants={approvedApplicants}
                shortlistedApplicants={shortlistedApplicantList}
                isLoading={roomSummaryLoading}
                onAddApplicants={handleAddApplicants}
                getGroupedApplicantsByFamily={getGroupedApplicantsByFamily}
                roomAvailabilities={tourApproval?.roomAvailabilities ?? []}
                reloadShortlistedApplicants={reloadShortlistedApplicants}
                venueSchedules={formData.venues}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                tourPublicId={id!}
                createdBy={Number(user?.id)}
                reloadRoomAllocations={reloadRoomAllocations}
                isReadOnly={isCancelled}
              />
            )}

            {/* Tab 6: Services */}
            {activeTab === "services" && (
              <Services
                applicants={serviceApplicants}
                serviceMaster={serviceMaster}
                applicantServices={applicantServices}
                setApplicantServices={setApplicantServices}
                saveApplicantServices={saveApplicantServices}
                carUnits={carUnits}
                setCarUnits={setCarUnits}
                dragOverApplicant={dragOverApplicant}
                setDragOverApplicant={setDragOverApplicant}
                dragOverCar={dragOverCar}
                setDragOverCar={setDragOverCar}
                sensors={sensors}
                handleRemoveService={handleRemoveService}
                handleServiceDragEnd={handleServiceDragEnd}
              />
            )}

            {activeTab === "itenary" && (
              <Excursions
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                tourPublicId={id!}
                venueSchedules={formData.venues}
                selectedApplicants={shortlistedApplicantList.map((a) => a.id)}
                saveTourAtraaf={saveTourAtraaf}
                venues={venuesFromApi}
                venueNames={Object.fromEntries(
                  venuesFromApi.map((venue) => [
                    venue.venueId,
                    venue.venueName,
                  ]),
                )}
                travelSegments={travelSegments}
                excursionsByDay={excursionsByDay}
                setExcursionsByDay={setExcursionsByDay}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                dropTargetDay={dropTargetDay}
                sensors={sensors}
                handleExcursionDragEnd={handleExcursionDragEnd}
                handleActivityDragStart={handleActivityDragStart}
                handleActivityDragEnd={handleActivityDragEnd}
                handleDayDragOver={handleDayDragOver}
                handleDayDragLeave={handleDayDragLeave}
                handleDayDrop={handleDayDrop}
                tourAtraaf={tourAtraaf}
                assignedAtraaf={assignedAtraaf}
                setAssignedAtraaf={setAssignedAtraaf}
              />
            )}

            {/* Tab 6: Overview */}
            {activeTab === "overview" && (
              <Overview
                tourName={formData.tourName}
                tourPublicId={tourData?.publicId ?? ""}
                selectedApplicants={shortlistedApplicantList.map((a) => a.id)}
                faizAccommodation={faizAccommodation}
                isFaizTour={tourData?.tourCategory === 3}
                venues={formData.venues}
                excursionsByDay={excursionsByDay}
                mockServices={services}
                arrivalPort={String(formData.arrivalAirport || "")}
                departurePort={String(formData.departureAirport || "")}
                arrivalFlightNumber={formData.arrivalFlightNumber}
                arrivalDate={formData.arrivalDate}
                arrivalTime={formData.arrivalTime}
                departureFlightNumber={formData.departureFlightNumber}
                departureDate={formData.departureDate}
                departureTime={formData.departureTime}
                portMasterData={ports.map((p) => ({
                  id: p.portId.toString(),
                  name: p.portName,
                }))}
                isPastCutoff={isPastCutoff}
                rooms={rooms}
                setRooms={setRooms}
                cancelledRoomIds={cancelledRoomIds}
                setCancelledRoomIds={setCancelledRoomIds}
                isTourCancelled={isTourCancelled}
                setIsTourCancelled={setIsTourCancelled}
                isReadOnly={isCancelled}
                isExternalTour={isExternalTour}
              />
            )}
          </div>

          {/* Approval Status Display */}
          {approvalStatus === "approved" && (
            <Card className="border-green-200 bg-green-50 mt-6">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Tour Approved
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                      This tour has been approved and the tour operator has been
                      notified.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Dialog
            open={showApplicantDialog}
            onOpenChange={setShowApplicantDialog}
          >
            <DialogContent className="!w-[80vw] !max-w-[1200px] !h-[90vh] p-0 overflow-hidden bg-white">
              <DialogHeader className="px-2 py-2 border-b">
                <DialogTitle>Add Applicant</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto">
                <VisaApplicantCard
                  tourOperatorId={tourData?.tourOperatorId}
                  forceVisaRequired={true}
                  onClose={() => setShowApplicantDialog(false)}
                  onSave={async (applicant) => {
                    const success = await handleAddApplicants([String(applicant.id)]);

                    if (success) {
                      setShowApplicantDialog(false);
                    }
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
          <SelectApplicants
            open={showSelectApplicantsDialog}
            onOpenChange={setShowSelectApplicantsDialog}
            applicants={applicants}
            availableApplicants={availableVisaApplicants}
            isLoading={loadingAvailableApplicants || applicantFilters !== debouncedApplicantFilters}
            filters={applicantFilters}
            onFiltersChange={setApplicantFilters}
            onSelect={async (selected) => {
              const applicantIds = selected.map((applicant) => applicant.id);

              const success = await handleAddApplicants(applicantIds);

              if (success) {
                setShowSelectApplicantsDialog(false);
              }
            }}
          />
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent className="sm:max-w-[500px] bg-white">
              <DialogHeader>
                <DialogTitle className="text-red-600">
                  Delete Applicant
                </DialogTitle>

                <DialogDescription>
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">
                    "{applicantToDelete?.fullName}"
                  </span>
                  ? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setApplicantToDelete(null);
                  }}
                >
                  No, Cancel
                </Button>

                <Button
                  onClick={confirmDeleteApplicant}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Yes, Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
}
